import { Lock } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiError } from '@/api/errors';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { usePrefersReducedMotion } from '@/app/providers/theme';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { cn } from '@/design/utils';
import { PIN_ATTEMPTS_BEFORE_PASSWORD } from '@/features/auth/idle';
import { LoginForm } from '@/features/auth/login';
import { useSessionContext } from '@/features/auth/session';

/**
 * Getting back in, over the top of whatever was on screen.
 *
 * **It is an overlay, never a navigation, and that is the whole point of it.** AD-54 locks a gate
 * terminal after five minutes, which will routinely happen in the middle of a half-filled
 * registration with somebody standing at the desk. A route change would discard that form; a modal
 * over it does not, because the route below stays mounted and keeps its state. The same is true of
 * a session that has expired outright: the operator signs in again in this dialog and finds their
 * work exactly where they left it.
 *
 * **It cannot be dismissed.** No close button, no outside click, no Escape: the three ways a Radix
 * dialog normally closes are each refused, because a lock somebody can click past is not a lock.
 *
 * **The PIN escalates to the password, and the count shown is this browser's own.** It has to be:
 * `attemptsRemaining` travels in the 2xx contract, and a REFUSED attempt is the AD-35 error body,
 * whose interpolation values do not cross the wire. So the modal counts down from the shipped
 * `security.pin_attempts_before_password` and the SERVER is authoritative — `AUTH_PIN_LOCKED` and
 * `AUTH_PIN_NOT_SET` both move straight to the password, whatever this side was counting. An
 * account with no PIN at all starts there.
 *
 * **A locked terminal can still be HANDED OVER, and it has to be.** `auth/matrix.ts` gives
 * `auth.switch_operator` a `bypass` for `idle_locked` and says why: the next person on shift cannot
 * be made to wait for the last one to come back and type their PIN, or a shift ends up sharing one
 * login — which is the whole thing AD-54 exists to prevent. So the lock offers a second way
 * through: the incoming operator signs in, exactly as they would from the header, and the outgoing
 * session ends in the same act. Without it the only routes past a locked screen are the incumbent's
 * own PIN and the twelve-hour absolute cap.
 *
 * **The only motion in this application is here, and it carries information.** A refused PIN shakes
 * the field, which says "wrong, try again" faster than re-reading a message does; an operator who
 * has asked their system for reduced motion gets the message alone. It is a CSS keyframe declared in
 * `design/tokens.css` rather than an animation runtime: one 240-millisecond shake is not worth a
 * library that measured at about a tenth of everything this application ships.
 */
export function ReauthenticationOverlay(): ReactNode {
  const { state } = useSessionContext();
  const open = state.status === 'locked' || state.status === 'expired';

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        closeLabel=""
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        aria-describedby={undefined}
        className="sm:max-w-md"
      >
        {state.status === 'expired' ? <ExpiredSession /> : null}
        {state.status === 'locked' ? (
          <LockedTerminal
            hasPin={state.session.user.hasPin}
            name={state.session.user.displayName}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** A session that is gone: the operator signs in again, and their work is still behind this. */
function ExpiredSession(): ReactNode {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Lock aria-hidden className="size-4" />
          {t('auth.signInTitle')}
        </DialogTitle>
        <DialogDescription>{t('auth.lockWorkPreserved')}</DialogDescription>
      </DialogHeader>
      <LoginForm />
    </>
  );
}

/** Which pair of sentences the unlock prompt uses. See the note at the call site. */
function pinPromptWording(
  usePassword: boolean,
  hasPin: boolean,
): {
  title: 'auth.lockTitle' | 'auth.lockPasswordTitle' | 'auth.noPinTitle';
  subtitle: 'auth.lockSubtitle' | 'auth.lockPasswordSubtitle' | 'auth.noPinSubtitle';
} {
  if (!usePassword) {
    return { title: 'auth.lockTitle', subtitle: 'auth.lockSubtitle' };
  }
  if (hasPin) {
    return { title: 'auth.lockPasswordTitle', subtitle: 'auth.lockPasswordSubtitle' };
  }
  return { title: 'auth.noPinTitle', subtitle: 'auth.noPinSubtitle' };
}

/**
 * A session that is alive and idle-locked: a PIN, escalating to the password.
 *
 * The operator's own name is on the dialog. The person coming back to a shared terminal needs to
 * know whose session they are about to unlock before they type anything into it — theirs, or the
 * one the last shift left open.
 */
function LockedTerminal({ hasPin, name }: Readonly<{ hasPin: boolean; name: string }>): ReactNode {
  const { t } = useTranslation();
  const [handingOver, setHandingOver] = useState(false);
  const { client, adopt } = useSessionContext();
  const describe = useApiErrorMessage();
  const reduceMotion = usePrefersReducedMotion();

  const [usePassword, setUsePassword] = useState(!hasPin);
  const [secret, setSecret] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(PIN_ATTEMPTS_BEFORE_PASSWORD);
  const [refusals, setRefusals] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    setFailure(null);
    setSubmitting(true);
    try {
      const unlocked = usePassword
        ? await client.request(apiRoutes.authPasswordUnlock, { body: { password: secret } })
        : await client.request(apiRoutes.authPinUnlock, { body: { pin: secret } });
      setSecret('');
      adopt(unlocked.session);
    } catch (error) {
      setSecret('');
      setRefusals((count) => count + 1);
      setFailure(describe(error));
      if (
        isApiError(error) &&
        (error.code === 'AUTH_PIN_LOCKED' || error.code === 'AUTH_PIN_NOT_SET')
      ) {
        setUsePassword(true);
        setAttemptsRemaining(0);
        return;
      }
      // **Computed outside the updater, because an updater must be pure.** React may run it more
      // than once — StrictMode does so deliberately — and a setter called from inside one is a side
      // effect in a function contracted to have none. It happens to be idempotent here, so nothing
      // misbehaves today; what is being removed is the shape, not a symptom.
      const next = Math.max(attemptsRemaining - 1, 0);
      setAttemptsRemaining(next);
      if (next === 0) {
        setUsePassword(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Three states, not two: the PIN prompt, the escalation to the password after the attempts are
  // spent, and an account that never had a PIN at all — which has to say something different,
  // because nothing has been refused and nothing has been used up.
  const wording = pinPromptWording(usePassword, hasPin);

  if (handingOver) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock aria-hidden className="size-4" />
            {t('session.switchOperatorTitle')}
          </DialogTitle>
          <DialogDescription>{t('session.switchOperatorBody')}</DialogDescription>
        </DialogHeader>
        <LoginForm route={apiRoutes.authSwitchOperator} />
        <Button
          variant="link"
          className="self-start px-0"
          onClick={() => {
            setHandingOver(false);
          }}
        >
          {t('auth.backToUnlock')}
        </Button>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Lock aria-hidden className="size-4" />
          {t(wording.title)}
        </DialogTitle>
        <DialogDescription>{t(wording.subtitle)}</DialogDescription>
      </DialogHeader>

      <p className="text-sm">
        <span className="text-muted-foreground">{t('session.signedInAs')}</span>{' '}
        <span className="font-medium">{name}</span>
      </p>

      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div
          className={cn('flex flex-col gap-2', !reduceMotion && refusals > 0 && 'animate-shake')}
          // Keyed on the refusal count, so each refusal REPLAYS the shake: a CSS animation on an
          // element that is already animating does not restart, and remounting the element is what
          // makes the second wrong PIN look different from the first. Reduced motion turns it off
          // entirely rather than shortening it, because the message says the same thing.
          key={refusals}
          data-testid="reauth-field"
          data-shake={reduceMotion ? 'off' : 'on'}
        >
          <Label htmlFor="reauth-secret">{t(usePassword ? 'auth.secretField' : 'auth.pin')}</Label>
          <Input
            id="reauth-secret"
            autoFocus
            type="password"
            inputMode={usePassword ? 'text' : 'numeric'}
            autoComplete={usePassword ? 'current-password' : 'off'}
            // Capped on the PIN branch ONLY, because this one input becomes the password field
            // after the escalation and a password has no length this product decides. Six is what
            // `pinSchema` and `crypto/password.ts`'s `PIN_LENGTH` both say a gate PIN is.
            {...(usePassword ? {} : { maxLength: 6 })}
            value={secret}
            onChange={(event) => {
              setSecret(event.target.value);
            }}
          />
        </div>

        {!usePassword && attemptsRemaining < PIN_ATTEMPTS_BEFORE_PASSWORD && (
          <p className="text-xs text-muted-foreground">
            {t('auth.lockAttemptsRemaining', { attempts: attemptsRemaining })}
          </p>
        )}

        {failure !== null && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground">{t('auth.lockWorkPreserved')}</p>

        <Button type="submit" disabled={submitting}>
          {t('auth.unlock')}
        </Button>
      </form>

      <Button
        variant="link"
        className="self-start px-0"
        onClick={() => {
          setHandingOver(true);
        }}
      >
        {t('session.switchOperator')}
      </Button>
    </>
  );
}
